import { CreateOrderData, Order, TrackingResponse } from '@/services/types/domain';
import { FrontendPaginatedResponse, PaginatedResponse } from '@/services/types/api';
import { httpClient } from '../http/client';
import { ApiResponse } from '@/services/types/api';

class OrdersApi {
    private readonly basePath = '/orders';

    /**
     * Obtener órdenes del usuario
     */
    async getOrders(filters: {
        page?: number;
        per_page?: number;
        status?: string;
        sort?: string;
    } = {}): Promise<FrontendPaginatedResponse<Order>> {
        //console.log("recibo", filters);
        const response = await httpClient.get<PaginatedResponse<Order>>(
            this.basePath,
            { params: filters }
        );

        return this.adaptBackendResponse(response.data);
    }

    /**
     * Obtener resumen de órdenes
     */
    async getOrdersSummary(): Promise<{
        total_orders: number;
        status_summary?: Record<string, number>;
        pending_orders: number;
        completed_orders: number;
        shipped_orders: number;
        total_spent: number;
        recent_orders: Order[];
    }> {
        const response = await httpClient.get<ApiResponse<any>>(
            `${this.basePath}/summary`
        );
        return response.data.data;
    }

    /**
     * Obtener una orden específica
     */
    async getOrderById(orderId: number): Promise<Order> {
        const response = await httpClient.get<ApiResponse<Order>>(
            `${this.basePath}/${orderId}`
        );
        return response.data.data;
    }

    /**
     * Crear nueva orden (checkout)
     */
    async createOrder2(data: {
        shipping_address: string;
        billing_address: string;
        payment_method: string;
        notes?: string;
    }): Promise<Order> {
        const response = await httpClient.post<ApiResponse<Order>>(
            this.basePath,
            data
        );
        return response.data.data;
    }
    async createOrder(orderData: CreateOrderData): Promise<Order> {
        const response = await httpClient.post<ApiResponse<Order>>(
            this.basePath,
            orderData
        );
        return response.data.data;
    }
    /* ejemplo:
    getOrder: async (orderId: number) => {
        const response = await api.get(`/orders/${orderId}`);
        return response.data;
    },

    getUserOrders: async () => {
        const response = await api.get('/user/orders');
        return response.data;
    }
        */
    /**
     * Cancelar una orden
     */
    async cancelOrder(orderId: number, reason?: string): Promise<Order> {
        const response = await httpClient.put<ApiResponse<Order>>(
            `${this.basePath}/${orderId}/cancel`,
            { reason }
        );
        return response.data.data;
    }
    // Nueva: solicitud de reembolso
    async requestRefund(
        orderId: number,
        payload: { reason: string; items: { order_item_id: number; quantity: number }[] }
    ): Promise<void> {
        await httpClient.post<ApiResponse<null>>(`/orders/${orderId}/refund-request`, payload);
    }
    /**
     * Confirmar recepción de una orden
     */
    async confirmOrderReceived(orderId: number): Promise<Order> {
        const response = await httpClient.put<ApiResponse<Order>>(
            `${this.basePath}/${orderId}/confirm`
        );
        return response.data.data;
    }

    /**
     * Repetir una orden (agregar todos los items al carrito)
     */
    async repeatOrder(orderId: number): Promise<{ message: string; cart_items: number }> {
        const response = await httpClient.post<ApiResponse<{ message: string; cart_items: number }>>(
            `${this.basePath}/${orderId}/repeat`
        );
        return response.data.data;
    }

    /**
     * Obtener órdenes pendientes (vendedor)
     */
    async getPendingOrders(): Promise<Order[]> {
        const response = await httpClient.get<ApiResponse<Order[]>>(
            `${this.basePath}/pending`
        );
        return response.data.data;
    }

    /**
     * Actualizar estado de una orden (vendedor/admin)
     */
    async updateOrderStatus(orderId: number, data: {
        status: 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
        notes?: string;
    }): Promise<Order> {
        const response = await httpClient.put<ApiResponse<Order>>(
            `${this.basePath}/${orderId}/status`,
            data
        );
        return response.data.data;
    }

    /**
     * Marcar orden como enviada (vendedor)
     */
    async markAsShipped(orderId: number, data: {
        tracking_number: string;
        carrier: string;
        notes?: string;
    }): Promise<Order> {
        const response = await httpClient.put<ApiResponse<Order>>(
            `${this.basePath}/${orderId}/ship`,
            data
        );
        return response.data.data;
    }
    async trackOrder(data: {
        order_number: string;
        email: string;
    }): Promise<TrackingResponse> {
        const response = await httpClient.post<ApiResponse<any>>(
            `/tracking`,
            data
        );
        return response.data.data;
    }
    /**
     * Obtener órdenes del vendedor
     */
    async getSellerOrders(filters: {
        page?: number;
        per_page?: number;
        status?: string;
        sort?: string;
    } = {}): Promise<FrontendPaginatedResponse<Order>> {
        const response = await httpClient.get<PaginatedResponse<Order>>(
            '/seller/orders',
            { params: filters }
        );

        return this.adaptBackendResponse(response.data);
    }

    /**
     * Obtener todas las órdenes (admin)
     */
    async getAllOrders(filters: {
        page?: number;
        per_page?: number;
        status?: string;
        user_id?: number;
        seller_id?: number;
        sort?: string;
    } = {}): Promise<FrontendPaginatedResponse<Order>> {
        const response = await httpClient.get<PaginatedResponse<Order>>(
            '/admin/orders',
            { params: filters }
        );

        return this.adaptBackendResponse(response.data);
    }

    /**
     * Adaptar respuesta paginada del backend al formato frontend
     */
    private adaptBackendResponse<T>(backendResponse: PaginatedResponse<T>): FrontendPaginatedResponse<T> {
        const { data, pagination } = backendResponse;

        return {
            data,
            meta: {
                current_page: pagination.current_page,
                from: (pagination.current_page - 1) * pagination.per_page + 1,
                last_page: pagination.total_pages,
                per_page: pagination.per_page,
                to: Math.min(pagination.current_page * pagination.per_page, pagination.total),
                total: pagination.total,
            },
            links: {
                first: `?page=1`,
                last: `?page=${pagination.total_pages}`,
                prev: pagination.current_page > 1 ? `?page=${pagination.current_page - 1}` : undefined,
                next: pagination.has_more ? `?page=${pagination.current_page + 1}` : undefined,
            },
        };
    }
}

// Instancia singleton
export const ordersApi = new OrdersApi();